import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import ApplePayMark from "chameleon/icons/apple-pay-mark";
import Ecp from "chameleon/icons/ecp";
import Gift from "chameleon/icons/gift";
import GooglePayMark from "chameleon/icons/google-pay-mark";
import PayByBank from "chameleon/icons/pay-by-bank";
import Paze from "chameleon/icons/paze";
import Sepa from "chameleon/icons/sepa";
import {
  CreditCardNetwork,
  DisplayCards,
} from "chameleon/lib/components/display-cards/display-cards";

import LpmIcon from "checkout-experience/components/payment-method/pay-options/lpm/lpm-icon/lpm-icon";
import { PaymentMethodDisplay } from "checkout-experience/components/payment-method/payment-method-summary-display";
import { getCardIcon } from "checkout-experience/components/payment-method/payment-method-summary-display-helpers/get-card-icon";
import { PaymentType } from "checkout-query-client/types/form-types";

export const useLeadingSlotForPaymentSummary = (
  paymentMethodSummary: PaymentMethodDisplay
) => {
  const { paymentType, network } = paymentMethodSummary;
  const { t } = useTranslation("combined");
  const CardIcon = getCardIcon(network);

  const translatedValues = useMemo(
    () => ({
      noCard: t("cardOptions.noCard"),
      americanExpress: t("cardOptions.americanExpress"),
      cvv: t("cardOptions.cvv"),
      discover: t("cardOptions.discover"),
      jcb: t("cardOptions.jcb"),
      visa: t("cardOptions.visa"),
      mastercard: t("cardOptions.mastercard"),
      maestro: t("cardOptions.maestro"),
      applePay: t("cardOptions.applePay"),
      unionPay: t("cardOptions.unionPay"),
      dinersClub: t("cardOptions.dinersClub"),
      cartesBancaires: t("cardOptions.cartesBancaires"),
      tooltipAriaLabel: t("tooltipAriaLabel"),
    }),
    [t]
  );

  switch (paymentType) {
    case PaymentType.creditCard:
      return (
        <DisplayCards
          supportedPaymentTypes={
            CardIcon ? [CardIcon as CreditCardNetwork] : []
          }
          isPaymentNameWithIcon={true}
          translatedValues={translatedValues}
        />
      );
    case PaymentType.eCheck:
      return (
        <Ecp sizeVariant={24} isRectangle aria-label={t("electronicCheck")} />
      );
    case PaymentType.sepa:
      return <Sepa sizeVariant={24} isRectangle aria-label={t("sepa")} />;
    case PaymentType.googlePay:
      return (
        <GooglePayMark
          sizeVariant={24}
          isRectangle
          aria-label={t("googlePay.googlePay")}
        />
      );
    case PaymentType.applePay:
      return (
        <ApplePayMark
          sizeVariant={24}
          isRectangle
          aria-label={t("applePay.applePay")}
        />
      );
    case PaymentType.paze:
      return (
        <Paze sizeVariant={24} isRectangle aria-label={t("paze.paze") ?? ""} />
      );
    case PaymentType.payByBank:
      return (
        <PayByBank
          sizeVariant={24}
          isRectangle
          aria-label={t("payByBank.payByBank")}
        />
      );
    case PaymentType.ideal:
    case PaymentType.alipay:
    case PaymentType.wechatPay:
    case PaymentType.trustly:
    case PaymentType.paypal:
      return <LpmIcon paymentType={paymentType} />;
    case PaymentType.giftCard:
      return (
        <Gift
          sizeVariant={24}
          isRectangle={true}
          aria-label={t("giftCard.title") ?? ""}
        />
      );
    default:
      return undefined;
  }
};
